import { inject, makeEnvironmentProviders, EnvironmentProviders, provideAppInitializer } from '@angular/core';
import { I18NEXT_SERVICE, I18NextLoadResult, ITranslationService, provideI18Next } from 'angular-i18next';
import { NZ_DATE_CONFIG, NZ_I18N, NzI18nService } from 'ng-zorro-antd/i18n';
import { I18N_SERVICE } from './tokens';
import {
  APP_LANGS,
  applyAppLangSideEffects,
  localeForAppLang,
  normalizeAppLang,
  readStoredAppLang,
  type AppLang,
} from './lang/app-lang';
import { registerAppLocaleData } from './lang/locale-data';
import { AppLanguageService } from './services/app-language.service';
import { I18nBundleLoader, I18nBundleService } from './services/i18n-bundle.service';

export type { AppLang, AppLangMeta } from './lang/app-lang';
export type { I18nBundleLoader } from './services/i18n-bundle.service';
export {
  APP_LANGS,
  APP_LANG_META,
  DEFAULT_APP_LANG,
  I18N_STORAGE_KEY,
  applyAppLangSideEffects,
  localeForAppLang,
  normalizeAppLang,
  readStoredAppLang,
} from './lang/app-lang';

function unwrapJsonResources<T>(value: T | { default: T }): T {
  if (value !== null && typeof value === 'object' && 'default' in value) {
    return (value as { default: T }).default;
  }
  return value as T;
}

export interface I18nResources {
  enResources: any;
  ruResources: any;
  hyResources: any;
  kkResources: any;
  kyResources: any;
  koResources: any;
  ttResources: any;
  ceResources: any;
  baResources: any;
  uzResources: any;
  tgResources: any;
}

export type I18nResourcesLoader = () => Promise<Partial<I18nResources>>;

function createAppInit(
  i18next: ITranslationService,
  nzI18n: NzI18nService,
  appLang: AppLanguageService,
  bundles: I18nBundleService,
  resourcesFn: I18nResourcesLoader,
  bundleLoader?: I18nBundleLoader,
): () => Promise<I18NextLoadResult> {
  return async (): Promise<I18NextLoadResult> => {
    bundles.registerLoader(bundleLoader);
    const {
      enResources,
      ruResources,
      hyResources,
      kkResources,
      kyResources,
      koResources,
      ttResources,
      ceResources,
      baResources,
      uzResources,
      tgResources,
    } = await resourcesFn();
    const initialLang = readStoredAppLang();
    const localeDataReady = registerAppLocaleData(initialLang);

    // Eager-ресурсы (обычно initial + ru-фолбэк) регистрируем сразу,
    // остальные языки догрузятся через I18nBundleService.ensure().
    const byLang: Array<[AppLang, unknown]> = [
      ['en', enResources],
      ['ru', ruResources],
      ['hy', hyResources],
      ['kk', kkResources],
      ['ky', kyResources],
      ['ko', koResources],
      ['tt', ttResources],
      ['ce', ceResources],
      ['ba', baResources],
      ['uz', uzResources],
      ['tg', tgResources],
    ];
    const resources: Record<string, { translation: any }> = {};
    for (const [lng, value] of byLang) {
      if (value === undefined) {
        continue;
      }
      resources[lng] = { translation: unwrapJsonResources(value) };
      bundles.markLoaded(lng);
    }

    const result = await i18next.init({
      lng: initialLang,
      supportedLngs: [...APP_LANGS],
      fallbackLng: 'ru',
      debug: false,
      // Словари используют одинарные скобки ({count}), поэтому включаем их
      // как разделители интерполяции — иначе t(key, params) вернёт сырой текст.
      // Существующие ручные .replace('{x}', ...) от этого не ломаются.
      interpolation: { prefix: '{', suffix: '}' },
      resources,
    });

    await localeDataReady;

    const syncLang = (lang: unknown): void => {
      const normalized = normalizeAppLang(lang);
      appLang.setLang(normalized);
      applyAppLangSideEffects(nzI18n, normalized);
    };

    syncLang(i18next.language ?? initialLang);
    i18next.events.languageChanged.subscribe((lang: string | null) => {
      syncLang(lang);
    });

    return result;
  };
}

export function provideI18n(resourcesFn: I18nResourcesLoader, bundleLoader?: I18nBundleLoader): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideI18Next(),
    { provide: I18N_SERVICE, useExisting: I18NEXT_SERVICE },
    { provide: NZ_I18N, useFactory: () => localeForAppLang(readStoredAppLang()) },
    {
      provide: NZ_DATE_CONFIG,
      useValue: {
        firstDayOfWeek: 1,
      },
    },
    provideAppInitializer(() =>
      createAppInit(
        inject(I18NEXT_SERVICE),
        inject(NzI18nService),
        inject(AppLanguageService),
        inject(I18nBundleService),
        resourcesFn,
        bundleLoader,
      )(),
    ),
  ]);
}
